/**
 * Página de Configurações do Sistema MEDICARE
 * Permite gerenciar configurações gerais do sistema
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  VStack,
  useColorModeValue,
  Card,
  CardBody,
  CardHeader,
  Grid,
  GridItem,
  Text,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Select,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Divider,
  Badge,
  IconButton,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormErrorMessage,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import {
  FiSave,
  FiRefreshCw,
  FiSettings,
  FiShield,
  FiMail,
  FiDatabase,
  FiCloud,
  FiEdit2,
  FiEye,
  FiEyeOff,
  FiTrash2,
  FiPlus,
  FiBell,
  FiMonitor,
  FiSend,
} from 'react-icons/fi';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useToastContext } from '../../contexts/ToastContext';
import { ConfiguracoesService } from '../../services/configuracoes';
import { EmailService, EmailTestData } from '../../services/email';

interface SystemConfig {
  id: string;
  categoria: string;
  chave: string;
  valor: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  descricao: string;
  obrigatorio: boolean;
  sensivel: boolean;
}

interface ConfigFormData {
  valor: string;
}

interface NewConfigFormData {
  chave: string;
  valor: string;
  tipo: 'string' | 'number' | 'boolean' | 'json';
  categoria: string;
  descricao?: string;
  editavel?: boolean;
}

interface EmailTestFormData {
  destinatario: string;
  assunto: string;
  conteudo: string;
}

const CONFIG_CATEGORIES = {
  geral: { label: 'Geral', icon: FiSettings, color: 'blue' },
  seguranca: { label: 'Segurança', icon: FiShield, color: 'red' },
  email: { label: 'Email/SMTP', icon: FiMail, color: 'green' },
  notificacoes: { label: 'Notificações', icon: FiBell, color: 'orange' },
  sistema: { label: 'Sistema', icon: FiMonitor, color: 'purple' },
};

export const ConfigurationsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('geral');
  const [selectedConfig, setSelectedConfig] = useState<SystemConfig | null>(null);
  const [showSensitiveValues, setShowSensitiveValues] = useState<Record<string, boolean>>({});
  const [configToDelete, setConfigToDelete] = useState<SystemConfig | null>(null);
  
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const { isOpen: isEmailTestModalOpen, onOpen: onEmailTestModalOpen, onClose: onEmailTestModalClose } = useDisclosure();
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  
  const { showSuccess, showError } = useToastContext();
  const queryClient = useQueryClient();
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const defaultTextColor = useColorModeValue('gray.800', 'gray.200');

  // Form para editar configuração
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<ConfigFormData>();

  // Form para adicionar nova configuração
  const {
    register: registerNew,
    handleSubmit: handleSubmitNew,
    formState: { errors: errorsNew, isSubmitting: isSubmittingNew },
    reset: resetNew,
  } = useForm<NewConfigFormData>();

  // Form para teste de email
  const {
    register: registerEmailTest,
    handleSubmit: handleSubmitEmailTest,
    formState: { errors: errorsEmailTest, isSubmitting: isSubmittingEmailTest },
    reset: resetEmailTest,
  } = useForm<EmailTestFormData>();

  // Query para buscar configurações
  const {
    data: configurations = [],
    isLoading,
    error,
  } = useQuery<SystemConfig[]>('configurations', async () => {
    try {
      const response = await ConfiguracoesService.listar();
      console.log('Resposta da API:', response);
      
      // A API retorna: { sucesso: true, dados: { configuracoes: [...], paginacao: {...} } }
      // O serviço encapsula isso em ApiResponse, então temos response.data = { sucesso: true, dados: {...} }
      const apiData = response.data as any;
      
      // Verificar se temos a estrutura esperada
      if (!apiData || !apiData.dados || !apiData.dados.configuracoes) {
        console.log('Estrutura de dados não encontrada:', response);
        console.log('Dados da API:', apiData);
        console.log('Dados disponíveis:', apiData?.dados);
        return [];
      }

      const configuracoes = apiData.dados.configuracoes;
      console.log('Configurações encontradas:', configuracoes);

      return configuracoes.map((config: any) => ({
        id: config.id,
        categoria: config.categoria,
        chave: config.chave,
        valor: config.valor,
        tipo: config.tipo,
        descricao: config.descricao || '',
        obrigatorio: true,
        sensivel: config.chave.toLowerCase().includes('password') || 
                 config.chave.toLowerCase().includes('secret') ||
                 config.chave.toLowerCase().includes('key'),
      }));
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      return [];
    }
  });

  // Mutation para atualizar configuração
  const updateConfigMutation = useMutation(
    ({ chave, valor }: { chave: string; valor: string }) => 
      ConfiguracoesService.atualizar(chave, { valor }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configurations');
        showSuccess('Configuração atualizada com sucesso!');
        onEditModalClose();
        reset();
      },
      onError: () => {
        showError('Erro ao atualizar configuração');
      },
    }
  );

  // Mutation para deletar configuração
  const deleteConfigMutation = useMutation(
    (chave: string) => ConfiguracoesService.deletar(chave),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configurations');
        showSuccess('Configuração removida com sucesso!');
        onDeleteAlertClose();
        setConfigToDelete(null);
      },
      onError: () => {
        showError('Erro ao remover configuração');
      },
    }
  );

  // Mutation para criar nova configuração
  const createConfigMutation = useMutation(
    (data: NewConfigFormData) => ConfiguracoesService.criar(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('configurations');
        showSuccess('Configuração criada com sucesso!');
        onAddModalClose();
        resetNew();
      },
      onError: () => {
        showError('Erro ao criar configuração');
      },
    }
  );

  // Mutation para teste de email
  const emailTestMutation = useMutation(
    (data: EmailTestData) => EmailService.testarEnvio(data),
    {
      onSuccess: (response) => {
        if (response.success && response.data?.sucesso) {
          showSuccess(response.data.mensagem || 'Email de teste enviado com sucesso!');
        } else {
          showError(response.data?.mensagem || 'Erro ao enviar email de teste');
        }
        onEmailTestModalClose();
        resetEmailTest();
      },
      onError: () => {
        showError('Erro ao enviar email de teste');
      },
    }
  );

  // Filtrar configurações por categoria
  const filteredConfigurations = configurations.filter(
    (config) => config.categoria === selectedCategory
  );

  const handleEditConfig = (config: SystemConfig) => {
    setSelectedConfig(config);
    setValue('valor', config.valor);
    onEditModalOpen();
  };

  const handleDeleteConfig = (config: SystemConfig) => {
    setConfigToDelete(config);
    onDeleteAlertOpen();
  };

  const confirmDelete = () => {
    if (configToDelete) {
      deleteConfigMutation.mutate(configToDelete.chave);
    }
  };

  const onSubmit = (data: ConfigFormData) => {
    if (selectedConfig) {
      updateConfigMutation.mutate({
        chave: selectedConfig.chave,
        valor: data.valor,
      });
    }
  };

  const onSubmitNew = (data: NewConfigFormData) => {
    createConfigMutation.mutate(data);
  };

  const toggleSensitiveVisibility = (configId: string) => {
    setShowSensitiveValues(prev => ({
      ...prev,
      [configId]: !prev[configId],
    }));
  };

  const formatValue = (config: SystemConfig) => {
    if (config.sensivel && !showSensitiveValues[config.id]) {
      return '••••••••••••';
    }
    
    switch (config.tipo) {
      case 'boolean':
        return config.valor === 'true' ? 'Sim' : 'Não';
      default:
        return config.valor;
    }
  };

  const getValueColor = (config: SystemConfig) => {
    if (config.tipo === 'boolean') {
      return config.valor === 'true' ? 'green.500' : 'red.500';
    }
    return defaultTextColor;
  };

  if (isLoading) {
    return <LoadingSpinner message="Carregando configurações..." />;
  }

  if (error) {
    return (
      <Container maxW="container.xl" py={8}>
        <Text color="red.500">Erro ao carregar configurações</Text>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        {/* Cabeçalho */}
        <Flex justify="space-between" align="center">
          <Heading size="lg">Configurações do Sistema</Heading>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiPlus />}
              colorScheme="medicare"
              onClick={onAddModalOpen}
            >
              Adicionar Configuração
            </Button>
            <Button
              leftIcon={<FiRefreshCw />}
              variant="outline"
              onClick={() => queryClient.invalidateQueries('configurations')}
            >
              Atualizar
            </Button>
          </HStack>
        </Flex>

        {/* Alerta de segurança */}
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle>Atenção!</AlertTitle>
            <AlertDescription>
              Alterações nas configurações podem afetar o funcionamento do sistema.
              Certifique-se de que você sabe o que está fazendo antes de fazer mudanças.
            </AlertDescription>
          </Box>
        </Alert>

        {/* Tabs por categoria */}
        <Tabs
          index={Object.keys(CONFIG_CATEGORIES).indexOf(selectedCategory)}
          onChange={(index) => setSelectedCategory(Object.keys(CONFIG_CATEGORIES)[index])}
          variant="enclosed"
        >
          <TabList>
            {Object.entries(CONFIG_CATEGORIES).map(([key, category]) => {
              const IconComponent = category.icon;
              const count = configurations.filter(c => c.categoria === key).length;
              return (
                <Tab
                  key={key}
                  _selected={{ bg: `${category.color}.50`, borderColor: `${category.color}.200` }}
                >
                  <HStack spacing={2}>
                    <IconComponent size={16} />
                    <Text>{category.label}</Text>
                    <Badge
                      colorScheme={category.color}
                      variant="subtle"
                      size="sm"
                    >
                      {count}
                    </Badge>
                  </HStack>
                </Tab>
              );
            })}
          </TabList>

          <TabPanels>
            {Object.keys(CONFIG_CATEGORIES).map((categoryKey) => (
              <TabPanel key={categoryKey} px={0}>
                {selectedCategory === categoryKey && (
                  <VStack spacing={6} align="stretch">
                    {/* Botão de teste de email - apenas na aba de email */}
                    {categoryKey === 'email' && (
                      <Card>
                        <CardBody>
                          <VStack spacing={4} align="stretch">
                            <HStack spacing={2}>
                              <FiSend />
                              <Text fontWeight="bold">Teste de Envio de Email</Text>
                            </HStack>
                            <Text fontSize="sm" color="gray.600">
                              Teste a configuração de email enviando um email de teste usando as configurações atuais.
                            </Text>
                            <Button
                              leftIcon={<FiSend />}
                              colorScheme="green"
                              size="sm"
                              onClick={onEmailTestModalOpen}
                              alignSelf="flex-start"
                            >
                              Testar Envio de Email
                            </Button>
                          </VStack>
                        </CardBody>
                      </Card>
                    )}
                    
                    {/* Grid de configurações */}
                    <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, 1fr)' }} gap={6}>
                    {filteredConfigurations.map((config) => (
                      <Card key={config.id}>
                        <CardHeader pb={3}>
                          <Flex justify="space-between" align="start">
                            <VStack align="start" spacing={1} flex={1}>
                              <HStack spacing={2}>
                                <Text fontWeight="bold" fontSize="sm">
                                  {config.chave}
                                </Text>
                                {config.obrigatorio && (
                                  <Badge colorScheme="red" variant="subtle" size="sm">
                                    Obrigatório
                                  </Badge>
                                )}
                                {config.sensivel && (
                                  <Badge colorScheme="orange" variant="subtle" size="sm">
                                    Sensível
                                  </Badge>
                                )}
                              </HStack>
                              <Text fontSize="xs" color="gray.500">
                                {config.descricao}
                              </Text>
                            </VStack>
                            <HStack spacing={1}>
                              {config.sensivel && (
                                <Tooltip label={showSensitiveValues[config.id] ? 'Ocultar' : 'Mostrar'}>
                                  <IconButton
                                    aria-label="Toggle visibility"
                                    icon={showSensitiveValues[config.id] ? <FiEyeOff /> : <FiEye />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => toggleSensitiveVisibility(config.id)}
                                  />
                                </Tooltip>
                              )}
                              <Tooltip label="Editar">
                                <IconButton
                                  aria-label="Editar configuração"
                                  icon={<FiEdit2 />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() => handleEditConfig(config)}
                                />
                              </Tooltip>
                              <Tooltip label="Remover">
                                <IconButton
                                  aria-label="Remover configuração"
                                  icon={<FiTrash2 />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() => handleDeleteConfig(config)}
                                />
                              </Tooltip>
                            </HStack>
                          </Flex>
                        </CardHeader>
                        <CardBody pt={0}>
                          <VStack align="start" spacing={2}>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                                Valor atual:
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="medium"
                                color={getValueColor(config)}
                                fontFamily={config.sensivel ? 'monospace' : 'inherit'}
                              >
                                {formatValue(config)}
                              </Text>
                            </Box>
                            
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                                Tipo:
                              </Text>
                              <Badge variant="outline" size="sm">
                                {config.tipo}
                              </Badge>
                            </Box>
                          </VStack>
                        </CardBody>
                      </Card>
                    ))}
                  </Grid>
                  </VStack>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </VStack>

      {/* Modal de edição */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Editar Configuração</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit(onSubmit)}>
            <ModalBody>
              {selectedConfig && (
                <VStack spacing={4}>
                  <Box w="full">
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Chave:
                    </Text>
                    <Text fontWeight="bold">{selectedConfig.chave}</Text>
                  </Box>
                  
                  <Box w="full">
                    <Text fontSize="sm" color="gray.600">
                      {selectedConfig.descricao}
                    </Text>
                  </Box>
                  
                  <Divider />
                  
                  <FormControl isInvalid={!!errors.valor}>
                    <FormLabel>Novo Valor</FormLabel>
                    {selectedConfig.tipo === 'boolean' ? (
                      <Select
                        {...register('valor', {
                          required: selectedConfig.obrigatorio ? 'Valor é obrigatório' : false,
                        })}
                      >
                        <option value="true">Sim (true)</option>
                        <option value="false">Não (false)</option>
                      </Select>
                    ) : selectedConfig.tipo === 'number' ? (
                      <NumberInput>
                        <NumberInputField
                          {...register('valor', {
                            required: selectedConfig.obrigatorio ? 'Valor é obrigatório' : false,
                            pattern: {
                              value: /^\d+$/,
                              message: 'Deve ser um número válido',
                            },
                          })}
                        />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    ) : selectedConfig.chave.includes('PASSWORD') || selectedConfig.chave.includes('SECRET') ? (
                      <Input
                        type="password"
                        {...register('valor', {
                          required: selectedConfig.obrigatorio ? 'Valor é obrigatório' : false,
                        })}
                      />
                    ) : (
                      <Textarea
                        {...register('valor', {
                          required: selectedConfig.obrigatorio ? 'Valor é obrigatório' : false,
                        })}
                        rows={3}
                      />
                    )}
                    <FormErrorMessage>
                      {errors.valor?.message}
                    </FormErrorMessage>
                  </FormControl>
                  
                  <HStack spacing={4} w="full">
                    <Box>
                      <Text fontSize="xs" color="gray.500">Tipo:</Text>
                      <Badge variant="outline">{selectedConfig.tipo}</Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Obrigatório:</Text>
                      <Badge colorScheme={selectedConfig.obrigatorio ? 'red' : 'gray'} variant="subtle">
                        {selectedConfig.obrigatorio ? 'Sim' : 'Não'}
                      </Badge>
                    </Box>
                    <Box>
                      <Text fontSize="xs" color="gray.500">Sensível:</Text>
                      <Badge colorScheme={selectedConfig.sensivel ? 'orange' : 'gray'} variant="subtle">
                        {selectedConfig.sensivel ? 'Sim' : 'Não'}
                      </Badge>
                    </Box>
                  </HStack>
                </VStack>
              )}
            </ModalBody>

            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onEditModalClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="medicare"
                leftIcon={<FiSave />}
                isLoading={isSubmitting}
              >
                Salvar
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Modal para adicionar nova configuração */}
      <Modal isOpen={isAddModalOpen} onClose={onAddModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Adicionar Nova Configuração</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmitNew(onSubmitNew)}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isInvalid={!!errorsNew.categoria}>
                  <FormLabel>Categoria</FormLabel>
                  <Select
                    placeholder="Selecione uma categoria"
                    {...registerNew('categoria', {
                      required: 'Categoria é obrigatória',
                    })}
                  >
                    {Object.entries(CONFIG_CATEGORIES).map(([key, category]) => (
                      <option key={key} value={key}>
                        {category.label}
                      </option>
                    ))}
                  </Select>
                  <FormErrorMessage>
                    {errorsNew.categoria?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsNew.chave}>
                  <FormLabel>Chave</FormLabel>
                  <Input
                    placeholder="Ex: site_do_sistema"
                    {...registerNew('chave', {
                      required: 'Chave é obrigatória',
                      pattern: {
                        value: /^[a-z_][a-z0-9_]*$/,
                        message: 'Chave deve conter apenas letras minúsculas, números e underscore',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errorsNew.chave?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsNew.tipo}>
                  <FormLabel>Tipo</FormLabel>
                  <Select
                    placeholder="Selecione o tipo"
                    {...registerNew('tipo', {
                      required: 'Tipo é obrigatório',
                    })}
                  >
                    <option value="string">Texto (string)</option>
                    <option value="number">Número (number)</option>
                    <option value="boolean">Verdadeiro/Falso (boolean)</option>
                    <option value="json">JSON</option>
                  </Select>
                  <FormErrorMessage>
                    {errorsNew.tipo?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsNew.valor}>
                  <FormLabel>Valor</FormLabel>
                  <Textarea
                    placeholder="Digite o valor da configuração"
                    {...registerNew('valor', {
                      required: 'Valor é obrigatório',
                    })}
                    rows={3}
                  />
                  <FormErrorMessage>
                    {errorsNew.valor?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Descrição (opcional)</FormLabel>
                  <Textarea
                    placeholder="Descreva para que serve esta configuração"
                    {...registerNew('descricao')}
                    rows={2}
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onAddModalClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="medicare"
                leftIcon={<FiSave />}
                isLoading={isSubmittingNew}
              >
                Criar Configuração
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* AlertDialog para confirmação de delete */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remover Configuração
            </AlertDialogHeader>

            <AlertDialogBody>
              {configToDelete && (
                <VStack align="start" spacing={3}>
                  <Text>
                    Tem certeza que deseja remover a configuração <strong>{configToDelete.chave}</strong>?
                  </Text>
                  <Box p={3} bg="red.50" borderRadius="md" w="full">
                    <Text fontSize="sm" color="red.600">
                      <strong>Atenção:</strong> Esta ação não pode ser desfeita e pode afetar o funcionamento do sistema.
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Categoria:
                    </Text>
                    <Text fontSize="sm">{CONFIG_CATEGORIES[configToDelete.categoria as keyof typeof CONFIG_CATEGORIES]?.label}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.500">
                      Valor atual:
                    </Text>
                    <Text fontSize="sm" fontFamily="monospace">
                      {configToDelete.sensivel ? '••••••••••••' : configToDelete.valor}
                    </Text>
                  </Box>
                </VStack>
              )}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={confirmDelete}
                ml={3}
                isLoading={deleteConfigMutation.isLoading}
              >
                Remover
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Modal de teste de email */}
      <Modal isOpen={isEmailTestModalOpen} onClose={onEmailTestModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Teste de Envio de Email</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmitEmailTest((data) => emailTestMutation.mutate(data))}>
            <ModalBody>
              <VStack spacing={4}>
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Teste de Configuração</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Este teste utilizará as configurações de email atuais do sistema para enviar um email de teste.
                    </AlertDescription>
                  </Box>
                </Alert>

                <FormControl isInvalid={!!errorsEmailTest.destinatario}>
                  <FormLabel>Destinatário</FormLabel>
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    {...registerEmailTest('destinatario', {
                      required: 'Destinatário é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email inválido',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errorsEmailTest.destinatario?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsEmailTest.assunto}>
                  <FormLabel>Assunto</FormLabel>
                  <Input
                    placeholder="Assunto do email de teste"
                    {...registerEmailTest('assunto', {
                      required: 'Assunto é obrigatório',
                      minLength: {
                        value: 3,
                        message: 'Assunto deve ter pelo menos 3 caracteres',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errorsEmailTest.assunto?.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errorsEmailTest.conteudo}>
                  <FormLabel>Conteúdo</FormLabel>
                  <Textarea
                    placeholder="Digite o conteúdo do email de teste..."
                    rows={6}
                    {...registerEmailTest('conteudo', {
                      required: 'Conteúdo é obrigatório',
                      minLength: {
                        value: 10,
                        message: 'Conteúdo deve ter pelo menos 10 caracteres',
                      },
                    })}
                  />
                  <FormErrorMessage>
                    {errorsEmailTest.conteudo?.message}
                  </FormErrorMessage>
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="outline" mr={3} onClick={onEmailTestModalClose}>
                Cancelar
              </Button>
              <Button
                type="submit"
                colorScheme="green"
                leftIcon={<FiSend />}
                isLoading={isSubmittingEmailTest}
              >
                Enviar Teste
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ConfigurationsPage;